import {APP_INITIALIZER, ApplicationConfig, importProvidersFrom, provideZonelessChangeDetection} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {provideAnimations} from '@angular/platform-browser/animations';
import {FormsModule} from '@angular/forms';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi} from '@angular/common/http';
import {registerLocaleData} from '@angular/common';
import en from '@angular/common/locales/en';
import {provideRouter} from '@angular/router';
import {en_US, NZ_I18N} from 'ng-zorro-antd/i18n';
import {NzNotificationService} from 'ng-zorro-antd/notification';
import {NzModalService} from 'ng-zorro-antd/modal';
import {DBConfig, NgxIndexedDBModule} from 'ngx-indexed-db';
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalInterceptor,
    MsalModule,
    MsalService,
} from '@azure/msal-angular';
import {provideUiTour} from 'ngx-ui-tour-md-menu';
import {NgxPermissionsModule} from 'ngx-permissions';
import {NgxEchartsModule} from 'ngx-echarts';

import {appRoutes} from './app.routes';
import {CoreModule} from './core/core.module';
import {baseUrlInterceptor} from './core/interceptors/base-url.interceptor';
import {errorInterceptor} from './core/interceptors/error.interceptor';
import {loadingInterceptor} from './core/interceptors/loading.interceptor';
import {initializeMsal, MSALGuardConfigFactory, MSALInstanceFactory, MSALInterceptorConfigFactory} from './auth-config';
import {IndexedDBConstants} from './shared/constants/indexedDBConstants';

const rowsDb: DBConfig = {
    name: 'pidbDataBase',
    version: 3,
    objectStoresMeta: [
        {
            store: IndexedDBConstants.pidbRows,
            storeConfig: {keyPath: 'name', autoIncrement: false},
            storeSchema: [],
        },
        {
            store: 'pidbHeader',
            storeConfig: {keyPath: 'id', autoIncrement: false},
            storeSchema: [],
        },
        {
            store: IndexedDBConstants.oldDocument,
            storeConfig: {keyPath: 'name', autoIncrement: false},
            storeSchema: [],
        },
    ],
};

registerLocaleData(en);

/**
 * Root application configuration migrated from the legacy AppModule. Wires
 * the functional router via `provideRouter(appRoutes)` (Phase 11 #8 — the
 * legacy `AppRoutingModule` / `RouterModule.forRoot` has been removed). Still
 * pulls in `CoreModule` and the third-party forRoot configs (Msal,
 * NgxIndexedDB, NgxPermissions, TourMatMenu, Animations) via
 * `importProvidersFrom`. The in-house HTTP interceptors (loading, error) are
 * wired functionally via `withInterceptors([...])`; MsalInterceptor is the
 * only DI-based interceptor left and stays in `HTTP_INTERCEPTORS`.
 *
 * Provider order is preserved verbatim from the original AppModule so the MSAL
 * APP_INITIALIZER still fires before MsalGuard / MsalInterceptor activate.
 */
export const appConfig: ApplicationConfig = {
    providers: [
        // Phase 12 Track D: enable zoneless change detection (Path B hybrid).
        // Angular ignores zone.js for CD; the polyfill stays loaded so
        // third-party libs that depend on NgZone-the-service (ag-grid,
        // ngx-ui-tour internals) continue to work. OnPush coverage is 94%
        // (51/54) — every component that can be OnPush already is, and the
        // 3 default-CD holdouts (ResultCodeSelector ngDoCheck, CodeTemplateCard
        // @Input mutation, ResultTable ag-grid) re-render via their own
        // internal triggers (HTTP subscribes, ag-grid's own CD model, parent
        // @Input ref-replacements).
        provideZonelessChangeDetection(),
        provideRouter(appRoutes),
        importProvidersFrom(
            BrowserModule,
            CoreModule,
            FormsModule,
            NgxIndexedDBModule.forRoot(rowsDb),
            MsalModule,
            NgxPermissionsModule.forRoot(),
            // ngx-echarts config token. Previously registered in the orphan
            // FileAnalysisModule via NgxEchartsModule.forRoot; that module is
            // no longer instantiated after the Phase 11 #8 functional routing
            // migration, so the provider has to live here at the root.
            NgxEchartsModule.forRoot({echarts: () => import('echarts')})
        ),
        provideUiTour({showProgress: false}),
        provideAnimations(),
        // Functional interceptors run BEFORE DI-based interceptors. Keep the
        // original chain order (loading → error → Msal) by listing the in-house
        // interceptors here and leaving MsalInterceptor in HTTP_INTERCEPTORS.
        provideHttpClient(
            withInterceptors([baseUrlInterceptor, loadingInterceptor, errorInterceptor]),
            withInterceptorsFromDi()
        ),
        {provide: NZ_I18N, useValue: en_US},
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        // msal-browser v3 requires PublicClientApplication.initialize() before
        // any other MSAL call (login, acquireToken, getAllAccounts, etc.).
        // APP_INITIALIZER blocks bootstrap until that promise resolves, so the
        // MsalInterceptor / MsalGuard / shell.component never race the init.
        {
            provide: APP_INITIALIZER,
            useFactory: initializeMsal,
            deps: [MSAL_INSTANCE],
            multi: true,
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        {
            provide: NzNotificationService,
        },
        NzModalService,
    ],
};
